import React, { useState, useEffect, useCallback } from 'react';
import {
    MdBuild, MdCheckCircle, MdSchedule, MdChat, MdClose, MdAdd,
    MdRefresh, MdPlayArrow, MdPause, MdDone, MdVisibility, MdInventory, MdWarning
} from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';

const STATUS_META = {
    open: { label: 'Open', bg: '#fef3c7', color: '#b45309' },
    assigned: { label: 'Assigned', bg: '#dbeafe', color: '#1d4ed8' },
    in_progress: { label: 'In Progress', bg: '#e0f2fe', color: '#0369a1' },
    on_hold: { label: 'On Hold', bg: '#f3f4f6', color: '#6b7280' },
    completed: { label: 'Completed', bg: '#dcfce7', color: '#15803d' },
    cancelled: { label: 'Cancelled', bg: '#fee2e2', color: '#b91c1c' },
};

const PRIORITY_META = {
    low: { label: 'Low', color: '#15803d', bg: '#f0fdf4' },
    medium: { label: 'Medium', color: '#b45309', bg: '#fef9c3' },
    high: { label: 'High', color: '#ea580c', bg: '#fff7ed' },
    critical: { label: 'Critical', color: '#b91c1c', bg: '#fee2e2' },
};

function StatusBadge({ status }) {
    const { t } = useLanguage();
    const m = STATUS_META[status] || { label: status, bg: '#f1f5f9', color: '#475569' };
    const label = status === 'cancelled' ? t('myTasks.statusCancelled') : m.label;
    return (
        <span style={{
            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
            background: m.bg, color: m.color, whiteSpace: 'nowrap'
        }}>
            {label}
        </span>
    );
}

function PriorityBadge({ priority }) {
    const m = PRIORITY_META[priority] || { label: priority, color: '#64748b', bg: '#f1f5f9' };
    return (
        <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
            background: m.bg, color: m.color, textTransform: 'uppercase', letterSpacing: '0.4px'
        }}>
            {m.label}
        </span>
    );
}

/* Inline action buttons based on current status */
function TaskActions({ wo, onUpdated }) {
    const [saving, setSaving] = useState(false);

    const changeStatus = async newStatus => {
        const comment = window.prompt(translateUiText('Add a progress report for this status change:'));
        if (!comment?.trim()) return;
        setSaving(true);
        try { await API.post(`/work-orders/${wo.id}/updates`, { status: newStatus, comment }); onUpdated(); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
            {wo.status === 'assigned' && (
                <button className="btn btn-sm btn-primary" title="Start Work"
                    disabled={saving} onClick={() => changeStatus('in_progress')}
                    style={{ fontSize: 11, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <MdPlayArrow size={13} /> Start
                </button>
            )}
            {wo.status === 'in_progress' && (
                <>
                    <button className="btn btn-sm" title="Mark Complete" disabled={saving}
                        onClick={() => changeStatus('completed')}
                        style={{
                            fontSize: 11, padding: '3px 8px', background: '#22c55e', color: '#fff',
                            border: 'none', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 3
                        }}>
                        <MdDone size={13} /> Done
                    </button>
                    <button className="btn btn-sm btn-light" title="Put on Hold" disabled={saving}
                        onClick={() => changeStatus('on_hold')}
                        style={{ fontSize: 11, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <MdPause size={13} /> Hold
                    </button>
                </>
            )}
            {wo.status === 'on_hold' && (
                <button className="btn btn-sm btn-primary" title="Resume Work" disabled={saving}
                    onClick={() => changeStatus('in_progress')}
                    style={{ fontSize: 11, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <MdPlayArrow size={13} /> Resume
                </button>
            )}
            {wo.status === 'completed' && (
                <span style={{ fontSize: 11, color: '#15803d', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <MdCheckCircle size={14} /> Done
                </span>
            )}
        </div>
    );
}

/* Type-aware "request something I need" form — restock a real inventory
   item, or request a brand-new asset. Works against either a Work Order
   or a Maintenance schedule, whichever `target` points at. Once a manager
   approves it, a vendor submits a quotation, and the quotation is
   approved, the backend automatically stocks in the item (or creates the
   asset) and attaches it to this same job — nothing to re-enter here. */
function RequestItemForm({ target, inventoryItems, assetCategories, onSubmitted }) {
    const { t } = useLanguage();
    const emptyForm = { request_type: 'new_asset', inventory_item_id: '', asset_name: '', asset_category_id: '', quantity: 1, justification: '' };
    const [form, setForm] = useState(emptyForm);
    const [sending, setSending] = useState(false);

    const submit = async event => {
        event.preventDefault();
        if (form.request_type === 'inventory_item' && !form.inventory_item_id) {
            alert(translateUiText('Select an inventory item to restock')); return;
        }
        if (form.request_type === 'new_asset' && !form.asset_name.trim()) {
            alert(translateUiText('Describe the asset you need')); return;
        }
        setSending(true);
        try {
            const payload = {
                ...target,
                request_type: form.request_type,
                quantity: form.quantity,
                justification: form.justification,
            };
            if (form.request_type === 'inventory_item') payload.inventory_item_id = form.inventory_item_id;
            else {
                payload.asset_name = form.asset_name;
                if (form.asset_category_id) payload.asset_category_id = form.asset_category_id;
            }
            await API.post('/asset-requests', payload);
            setForm(emptyForm);
            onSubmitted && onSubmitted();
        } catch (err) {
            const detail = err.response?.data?.error || err.response?.data?.message;
            alert(detail || `${translateUiText('Request failed')} (${err.response?.status || translateUiText('network error')}).`);
        }
        finally { setSending(false); }
    };

    return (
        <form onSubmit={submit} style={{ background: '#f5f9ff', padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('myTasks.requestItemHeading')}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button type="button"
                    className={`btn btn-sm ${form.request_type === 'new_asset' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setForm({ ...form, request_type: 'new_asset' })}>
                    {t('myTasks.newAssetTab')}
                </button>
                <button type="button"
                    className={`btn btn-sm ${form.request_type === 'inventory_item' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setForm({ ...form, request_type: 'inventory_item' })}>
                    <MdInventory size={13} style={{ marginRight: 3 }} />{t('myTasks.restockItemTab')}
                </button>
            </div>
            <div className="row g-2">
                {form.request_type === 'inventory_item' ? (
                    <div className="col-md-7">
                        <select className="form-select form-select-sm" value={form.inventory_item_id}
                            onChange={e => setForm({ ...form, inventory_item_id: e.target.value })} required>
                            <option value="">{t('myTasks.selectInventoryItem')}</option>
                            {inventoryItems.map(i => (
                                <option key={i.id} value={i.id}>
                                    {i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <>
                        <div className="col-md-7">
                            <input className="form-control form-control-sm" placeholder={t('myTasks.assetNeededPlaceholder')}
                                value={form.asset_name} onChange={e => setForm({ ...form, asset_name: e.target.value })} required />
                        </div>
                        {!!assetCategories.length && (
                            <div className="col-md-5">
                                <select className="form-select form-select-sm" value={form.asset_category_id}
                                    onChange={e => setForm({ ...form, asset_category_id: e.target.value })}>
                                    <option value="">{t('myTasks.categoryOptional')}</option>
                                    {assetCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}
                    </>
                )}
                <div className="col-md-5">
                    <input type="number" min="1" className="form-control form-control-sm" placeholder="Quantity"
                        value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} required />
                </div>
                <div className="col-12">
                    <textarea className="form-control form-control-sm" rows="2" placeholder="Why is it needed?"
                        value={form.justification} onChange={e => setForm({ ...form, justification: e.target.value })} />
                </div>
            </div>
            <button className="btn btn-sm btn-primary mt-2" disabled={sending}>{sending ? 'Sending...' : t('myTasks.sendRequestButton')}</button>
        </form>
    );
}

/* Detail / comment drawer */
function TaskDetailModal({ wo, onClose, onUpdated, inventoryItems, assetCategories }) {
    const { t } = useLanguage();
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [updates, setUpdates] = useState(wo.updates || []);
    const [reportStatus, setReportStatus] = useState(wo.status === 'assigned' ? 'in_progress' : wo.status);

    // Items actually used from existing stock — distinct from RequestItemForm
    // below, which is only for something NOT currently available. The
    // assignee picks real inventory here the same way a manager already can
    // from the Work Orders page; the backend now allows it for whoever the
    // job is assigned to, not just admin/manager.
    const [parts, setParts] = useState(wo.parts_needed || []);
    const [newPart, setNewPart] = useState({ item_id: '', quantity_needed: '' });
    const [partsBusy, setPartsBusy] = useState(false);

    const refreshParts = async () => {
        const { data } = await API.get(`/work-orders/${wo.id}`);
        setParts(data.parts_needed || []);
    };

    const addPart = async () => {
        if (!newPart.item_id || !newPart.quantity_needed) return;
        setPartsBusy(true);
        try {
            await API.post('/inventory/requirements', {
                work_order_id: wo.id, item_id: newPart.item_id, quantity_needed: newPart.quantity_needed
            });
            setNewPart({ item_id: '', quantity_needed: '' });
            await refreshParts();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not add item')); }
        finally { setPartsBusy(false); }
    };

    const removePart = async reqId => {
        setPartsBusy(true);
        try {
            await API.delete(`/inventory/requirements/${reqId}`);
            await refreshParts();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not remove item')); }
        finally { setPartsBusy(false); }
    };

    const handleReport = async event => {
        event.preventDefault();
        if (!comment.trim()) return;
        setSaving(true);
        try {
            await API.post(`/work-orders/${wo.id}/updates`, {
                comment, status: reportStatus, hours_spent: 0
            });
            const { data } = await API.get(`/work-orders/${wo.id}`);
            setUpdates(data.updates || []);
            setComment('');
            onUpdated();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Progress report failed')); }
        finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px', borderBottom: '1px solid #e2e8f0'
                }}>
                    <div>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0ea5e9', fontWeight: 600 }}>
                            {wo.wo_number}
                        </span>
                        <h6 style={{ margin: '2px 0 0', fontWeight: 700 }}>{wo.title}</h6>
                    </div>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}>
                        <MdClose size={20} />
                    </button>
                </div>
                <div style={{ padding: 20 }}>
                    {/* Meta info grid */}
                    <div className="row g-2 mb-3" style={{ fontSize: 13 }}>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Status</span><br /><StatusBadge status={wo.status} /></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Priority</span><br /><PriorityBadge priority={wo.priority} /></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Location</span><br /><span style={{ fontWeight: 500 }}>{wo.building_name || '—'}{wo.space_name ? ` › ${wo.space_name}` : ''}</span></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Due Date</span><br /><span style={{ fontWeight: 500 }}>{wo.due_date || '—'}</span></div>
                        {wo.asset_name && <div className="col-12"><span style={{ color: '#94a3b8' }}>Asset</span><br /><span style={{ fontWeight: 500 }}>{wo.asset_name}</span></div>}
                        {wo.description && <div className="col-12"><span style={{ color: '#94a3b8' }}>Description</span><br /><span>{wo.description}</span></div>}
                        {wo.notes && <div className="col-12"><span style={{ color: '#94a3b8' }}>Notes</span><br /><span>{wo.notes}</span></div>}
                    </div>

                    {/* Quick status change */}
                    <div style={{ marginBottom: 16 }}>
                        <TaskActions wo={wo} onUpdated={onUpdated} />
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14, marginBottom: 16 }}>
                        <h6 style={{ fontSize: 13 }}><MdInventory style={{ marginRight: 6 }} />Items used from stock ({parts.length})</h6>
                        {parts.map(p => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#f8fafc', padding: '8px 10px', marginBottom: 6, borderRadius: 6, fontSize: 12 }}>
                                <div>
                                    <strong>{p.item_name}</strong> <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{p.item_code}</span>
                                    <div style={{ color: '#64748b' }}>Need {p.quantity_needed}{p.unit ? ` ${p.unit}` : ''} · Have {p.available}{p.unit ? ` ${p.unit}` : ''}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {p.fulfilled ? (
                                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#e0f2fe', color: '#0369a1' }}>{t('myTasks.partDeducted')}</span>
                                    ) : p.ready ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#dcfce7', color: '#15803d' }}><MdCheckCircle size={12} /> {t('myTasks.partReady')}</span>
                                    ) : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#fee2e2', color: '#b91c1c' }}><MdWarning size={12} /> Short</span>
                                    )}
                                    {!p.fulfilled && (
                                        <button type="button" className="btn btn-sm btn-light" disabled={partsBusy} onClick={() => removePart(p.id)} title="Remove"><MdClose size={13} /></button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {!parts.length && <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>{t('myTasks.noPartsLogged')}</div>}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                            <select className="form-select form-select-sm" style={{ flex: 1 }} value={newPart.item_id} onChange={e => setNewPart({ ...newPart, item_id: e.target.value })}>
                                <option value="">Select item in stock...</option>
                                {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock</option>)}
                            </select>
                            <input type="number" className="form-control form-control-sm" style={{ width: 90 }} min={0.01} step="0.01" placeholder="Qty" value={newPart.quantity_needed} onChange={e => setNewPart({ ...newPart, quantity_needed: e.target.value })} />
                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={partsBusy || !newPart.item_id || !newPart.quantity_needed} onClick={addPart}>Add</button>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{t('myTasks.autoDeductNote')}</div>
                    </div>

                    <RequestItemForm target={{ work_order_id: wo.id }} inventoryItems={inventoryItems}
                        assetCategories={assetCategories} onSubmitted={onUpdated} />

                    {/* Activity log */}
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                            <MdChat style={{ marginRight: 6 }} />{t('myTasks.activityLog')} ({updates.length})
                        </div>
                        <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 10 }}>
                            {updates.map(u => (
                                <div key={u.id} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                        <span style={{ fontWeight: 600, color: '#0ea5e9' }}>{u.user_name}</span>
                                        <span style={{ color: '#94a3b8' }}>{new Date(u.created_at).toLocaleString()}</span>
                                    </div>
                                    <div style={{ fontSize: 13, marginTop: 2 }}>{u.comment}</div>
                                </div>
                            ))}
                            {!updates.length && <div style={{ color: '#94a3b8', fontSize: 13 }}>No activity yet.</div>}
                        </div>
                        <form onSubmit={handleReport}>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <select className="form-select form-select-sm" value={reportStatus} onChange={e => setReportStatus(e.target.value)}>
                                    {wo.status !== 'completed' && <><option value="in_progress">In progress</option><option value="on_hold">On hold</option></>}
                                    <option value="completed">Completed</option>
                                </select>
                                <input className="form-control form-control-sm" placeholder="What changed? What was done?"
                                    value={comment} onChange={e => setComment(e.target.value)} />
                            </div>
                            <button className="btn btn-primary btn-sm" type="submit" disabled={saving || !comment.trim()}>
                                {saving ? 'Sending...' : t('myTasks.submitProgressReport')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* Same request form, opened against an assigned Maintenance schedule
   instead of a Work Order — schedules have no status/activity log of
   their own here, so this stays a small focused modal. */
function ScheduleRequestModal({ schedule, onClose, inventoryItems, assetCategories, onSubmitted }) {
    const { t } = useLanguage();
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px', borderBottom: '1px solid #e2e8f0'
                }}>
                    <div>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Maintenance</span>
                        <h6 style={{ margin: '2px 0 0', fontWeight: 700 }}>{schedule.title}</h6>
                    </div>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}>
                        <MdClose size={20} />
                    </button>
                </div>
                <div style={{ padding: 20 }}>
                    <div className="row g-2 mb-3" style={{ fontSize: 13 }}>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Location</span><br /><span style={{ fontWeight: 500 }}>{schedule.building_name || '—'}{schedule.space_name ? ` › ${schedule.space_name}` : ''}</span></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>{t('myTasks.nextDue')}</span><br /><span style={{ fontWeight: 500 }}>{schedule.next_due || '—'}</span></div>
                        {schedule.asset_name && <div className="col-12"><span style={{ color: '#94a3b8' }}>Asset</span><br /><span style={{ fontWeight: 500 }}>{schedule.asset_name}</span></div>}
                    </div>
                    <RequestItemForm target={{ maintenance_schedule_id: schedule.id }} inventoryItems={inventoryItems}
                        assetCategories={assetCategories} onSubmitted={() => { onSubmitted(); onClose(); }} />
                </div>
            </div>
        </div>
    );
}

/* Lets the person a maintenance schedule is assigned to actually record
   the visit — description, findings, and any inventory items used from
   stock — instead of only being able to request something that's missing.
   Submitting logs it as done; if no next-due date is set here, the backend
   auto-advances the schedule on its own frequency (see Maintenance.js). */
function ScheduleLogModal({ schedule, onClose, inventoryItems, onSubmitted }) {
    const { t } = useLanguage();
    const [description, setDescription] = useState('');
    const [findings, setFindings] = useState('');
    const [parts, setParts] = useState([]);
    const [newPart, setNewPart] = useState({ item_id: '', quantity: '' });
    const [saving, setSaving] = useState(false);

    const addPart = () => {
        if (!newPart.item_id || !newPart.quantity) return;
        setParts([...parts, newPart]);
        setNewPart({ item_id: '', quantity: '' });
    };
    const removePart = idx => setParts(parts.filter((_, i) => i !== idx));

    const submit = async confirmShortage => {
        setSaving(true);
        try {
            await API.post('/maintenance/logs', {
                schedule_id: schedule.id,
                asset_id: schedule.asset_id || '',
                description, findings, parts,
                confirm_shortage: confirmShortage,
            });
            onSubmitted();
            onClose();
        } catch (err) {
            const shortage = err.response?.data?.shortage;
            if (err.response?.status === 409 && shortage) {
                const lines = shortage.map(s => `• ${s.name}: need ${s.qty}, have ${s.available}`).join('\n');
                if (window.confirm(`${err.response.data.error}\n\n${lines}\n\nLog it anyway?`)) {
                    await submit(true);
                    return;
                }
            } else {
                alert(err.response?.data?.error || translateUiText('Save failed'));
            }
        } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px', borderBottom: '1px solid #e2e8f0'
                }}>
                    <div>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{t('myTasks.logMaintenanceWork')}</span>
                        <h6 style={{ margin: '2px 0 0', fontWeight: 700 }}>{schedule.title}</h6>
                    </div>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}>
                        <MdClose size={20} />
                    </button>
                </div>
                <div style={{ padding: 20 }}>
                    <div className="mb-2">
                        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>What was done</label>
                        <textarea className="form-control form-control-sm" rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="What was done..." />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Findings</label>
                        <textarea className="form-control form-control-sm" rows={2} value={findings} onChange={e => setFindings(e.target.value)} />
                    </div>
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginBottom: 12 }}>
                        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('myTasks.itemsUsedFromStock')}</label>
                        {parts.map((p, idx) => {
                            const item = inventoryItems.find(i => String(i.id) === String(p.item_id));
                            return (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#f8fafc', padding: '6px 10px', marginBottom: 6, borderRadius: 6, fontSize: 12 }}>
                                    <div>{item?.name || 'Item'} × {p.quantity}</div>
                                    <button type="button" className="btn btn-sm btn-light" onClick={() => removePart(idx)}><MdClose size={12} /></button>
                                </div>
                            );
                        })}
                        <div style={{ display: 'flex', gap: 6 }}>
                            <select className="form-select form-select-sm" style={{ flex: 1 }} value={newPart.item_id} onChange={e => setNewPart({ ...newPart, item_id: e.target.value })}>
                                <option value="">Select item in stock...</option>
                                {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock</option>)}
                            </select>
                            <input type="number" className="form-control form-control-sm" style={{ width: 80 }} min={0.01} step="0.01" placeholder="Qty" value={newPart.quantity} onChange={e => setNewPart({ ...newPart, quantity: e.target.value })} />
                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={!newPart.item_id || !newPart.quantity} onClick={addPart}>Add</button>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{t('myTasks.needSomethingElseNote')}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
                        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => submit(false)}>{saving ? 'Saving...' : t('myTasks.logAsDone')}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function MyTasks() {
    const { t } = useLanguage();
    const { user, isAdmin, isManager } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [selected, setSelected] = useState(null);
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [ticketForm, setTicketForm] = useState({ title: '', description: '', type: 'corrective', priority: 'medium' });
    const [savingTicket, setSavingTicket] = useState(false);
    const [view, setView] = useState('work_orders');
    const [schedules, setSchedules] = useState([]);
    const [loadingSchedules, setLoadingSchedules] = useState(true);
    const [scheduleForRequest, setScheduleForRequest] = useState(null);
    const [scheduleForLog, setScheduleForLog] = useState(null);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [assetCategories, setAssetCategories] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filterStatus ? { status: filterStatus } : {};
            const { data } = await API.get('/work-orders', { params });
            // Fetch updates for each task
            const detailed = await Promise.all(
                data.map(wo => API.get(`/work-orders/${wo.id}`).then(r => r.data))
            );
            setTasks(detailed);
        } finally { setLoading(false); }
    }, [filterStatus]);

    const loadSchedules = useCallback(async () => {
        setLoadingSchedules(true);
        try {
            const { data } = await API.get('/maintenance/schedules');
            setSchedules((data || []).filter(s => s.assigned_to === user.id));
        } catch { setSchedules([]); }
        finally { setLoadingSchedules(false); }
    }, [user.id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSchedules(); }, [loadSchedules]);
    useEffect(() => {
        API.get('/inventory').then(r => setInventoryItems(r.data)).catch(() => {});
        API.get('/assets/categories').then(r => setAssetCategories(r.data)).catch(() => {});
    }, []);

    const generateTicket = async event => {
        event.preventDefault();
        setSavingTicket(true);
        try {
            await API.post('/work-orders', ticketForm);
            setTicketForm({ title: '', description: '', type: 'corrective', priority: 'medium' });
            setShowTicketModal(false);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Ticket could not be generated'));
        } finally { setSavingTicket(false); }
    };

    const jobLabel = jt => jt ? jt.charAt(0).toUpperCase() + jt.slice(1) : 'Employee';

    const counts = {
        all: tasks.length,
        pending: tasks.filter(t => ['open', 'assigned'].includes(t.status)).length,
        in_progress: tasks.filter(t => t.status === 'in_progress').length,
        completed: tasks.filter(t => t.status === 'completed').length,
        on_hold: tasks.filter(t => t.status === 'on_hold').length,
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>My Tasks</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {user.name} — {jobLabel(user.job_title)}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {(isAdmin || isManager) && (
                        <button className="btn btn-primary btn-sm" onClick={() => setShowTicketModal(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdAdd size={16} /> Work Order
                        </button>
                    )}
                    <button className="btn btn-light btn-sm" onClick={load}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MdRefresh size={16} /> Refresh
                    </button>
                </div>
            </div>

            {/* Work Orders vs Maintenance view toggle */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                <button className={`btn btn-sm ${view === 'work_orders' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setView('work_orders')}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdBuild size={15} /> Work Orders ({counts.all})
                </button>
                <button className={`btn btn-sm ${view === 'maintenance' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setView('maintenance')}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdSchedule size={15} /> Maintenance ({schedules.length})
                </button>
            </div>

            {view === 'work_orders' && <>
            {/* Summary stats */}
            <div className="row g-3 mb-4">
                {[
                    { label: 'Total', value: counts.all, icon: <MdBuild />, cls: 'blue' },
                    { label: 'Pending', value: counts.pending, icon: <MdSchedule />, cls: 'yellow' },
                    { label: 'In Progress', value: counts.in_progress, icon: <MdPlayArrow />, cls: 'teal' },
                    { label: 'Completed', value: counts.completed, icon: <MdCheckCircle />, cls: 'green' },
                ].map(s => (
                    <div key={s.label} className="col-6 col-md-3">
                        <div className="stat-card">
                            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
                            <div><div className="stat-value">{s.value}</div><div className="stat-label">{s.label}</div></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {[
                    { value: '', label: `All (${counts.all})` },
                    { value: 'assigned', label: `Assigned (${counts.pending})` },
                    { value: 'in_progress', label: `In Progress (${counts.in_progress})` },
                    { value: 'on_hold', label: `On Hold (${counts.on_hold})` },
                    { value: 'completed', label: `Completed (${counts.completed})` },
                ].map(tab => (
                    <button key={tab.value} onClick={() => setFilterStatus(tab.value)} style={{
                        padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer',
                        fontWeight: 500, fontSize: 13,
                        color: filterStatus === tab.value ? '#0ea5e9' : '#64748b',
                        borderBottom: filterStatus === tab.value ? '2px solid #0ea5e9' : '2px solid transparent',
                    }}>{tab.label}</button>
                ))}
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? (
                        <div className="spinner-wrap"><div className="spinner-border" /></div>
                    ) : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 90 }}>WO #</th>
                                    <th>Title</th>
                                    <th style={{ width: 80 }}>Priority</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th>Location</th>
                                    <th style={{ width: 90 }}>Due Date</th>
                                    <th style={{ width: 110 }}>Actions</th>
                                    <th style={{ width: 60 }}>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.map(wo => (
                                    <tr key={wo.id}>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#0ea5e9', fontWeight: 600 }}>
                                                {wo.wo_number}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 500, fontSize: 13 }}>{wo.title}</div>
                                            {wo.asset_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>🔧 {wo.asset_name}</div>}
                                        </td>
                                        <td><PriorityBadge priority={wo.priority} /></td>
                                        <td><StatusBadge status={wo.status} /></td>
                                        <td style={{ fontSize: 12 }}>
                                            {wo.building_name && <div>{wo.building_name}</div>}
                                            {wo.space_name && <div style={{ color: '#94a3b8' }}>{wo.space_name}</div>}
                                        </td>
                                        <td style={{ fontSize: 12, color: wo.due_date && new Date(wo.due_date) < new Date() && wo.status !== 'completed' ? '#ef4444' : '#64748b' }}>
                                            {wo.due_date || '—'}
                                        </td>
                                        <td><TaskActions wo={wo} onUpdated={load} /></td>
                                        <td>
                                            <button className="btn btn-sm btn-light" title="View detail"
                                                onClick={async () => {
                                                    const { data } = await API.get(`/work-orders/${wo.id}`);
                                                    setSelected(data);
                                                }}>
                                                <MdVisibility size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!tasks.length && (
                                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 50, color: '#94a3b8' }}>
                                        <MdCheckCircle size={36} style={{ color: '#22c55e', marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                        No tasks assigned to you yet
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            </>}

            {view === 'maintenance' && (
                <div className="page-card">
                    <div style={{ overflowX: 'auto' }}>
                        {loadingSchedules ? (
                            <div className="spinner-wrap"><div className="spinner-border" /></div>
                        ) : (
                            <table className="fms-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th style={{ width: 130 }}>Frequency</th>
                                        <th>Location / Asset</th>
                                        <th style={{ width: 100 }}>{t('myTasks.nextDue')}</th>
                                        <th style={{ width: 100 }}>Parts</th>
                                        <th style={{ width: 230 }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {schedules.map(s => (
                                        <tr key={s.id}>
                                            <td style={{ fontWeight: 500, fontSize: 13 }}>{s.title}</td>
                                            <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{(s.frequency || '').replace('_', ' ')}</td>
                                            <td style={{ fontSize: 12 }}>
                                                {s.building_name && <div>{s.building_name}{s.space_name ? ` › ${s.space_name}` : ''}</div>}
                                                {s.asset_name && <div style={{ color: '#94a3b8' }}>🔧 {s.asset_name}</div>}
                                            </td>
                                            <td style={{ fontSize: 12, color: s.next_due && new Date(s.next_due) < new Date() ? '#ef4444' : '#64748b' }}>
                                                {s.next_due || '—'}
                                            </td>
                                            <td>
                                                {s.parts_total > 0 ? (
                                                    s.parts_ready ? (
                                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d', display: 'flex', alignItems: 'center', gap: 3 }}>
                                                            <MdCheckCircle size={13} /> Ready
                                                        </span>
                                                    ) : (
                                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 3 }}>
                                                            <MdWarning size={13} /> Short
                                                        </span>
                                                    )
                                                ) : <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    <button className="btn btn-sm btn-primary" onClick={() => setScheduleForLog(s)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                                        <MdCheckCircle size={13} /> {t('myTasks.logWorkButton')}
                                                    </button>
                                                    <button className="btn btn-sm btn-light" onClick={() => setScheduleForRequest(s)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                                        <MdInventory size={13} /> {t('myTasks.requestItemAssetButton')}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!schedules.length && (
                                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: 50, color: '#94a3b8' }}>
                                            <MdSchedule size={36} style={{ color: '#0ea5e9', marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                            {t('myTasks.noMaintenanceJobs')}
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {selected && (
                <TaskDetailModal wo={selected} onClose={() => setSelected(null)} onUpdated={() => { load(); setSelected(null); }}
                    inventoryItems={inventoryItems} assetCategories={assetCategories} />
            )}

            {scheduleForRequest && (
                <ScheduleRequestModal schedule={scheduleForRequest} onClose={() => setScheduleForRequest(null)}
                    inventoryItems={inventoryItems} assetCategories={assetCategories} onSubmitted={loadSchedules} />
            )}

            {scheduleForLog && (
                <ScheduleLogModal schedule={scheduleForLog} onClose={() => setScheduleForLog(null)}
                    inventoryItems={inventoryItems} onSubmitted={loadSchedules} />
            )}

            {showTicketModal && (isAdmin || isManager) && (
                <div className="modal-overlay" onClick={() => setShowTicketModal(false)}>
                    <div className="modal-box" onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                            <h6 style={{ margin: 0, fontWeight: 600 }}>Work Order</h6>
                            <button onClick={() => setShowTicketModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}><MdClose size={20} /></button>
                        </div>
                        <form onSubmit={generateTicket} style={{ padding: 20 }}>
                            <div className="mb-3"><label className="form-label fw-semibold">What needs attention? *</label><input className="form-control" value={ticketForm.title} onChange={e => setTicketForm({ ...ticketForm, title: e.target.value })} required /></div>
                            <div className="mb-3"><label className="form-label fw-semibold">Description *</label><textarea className="form-control" rows={4} value={ticketForm.description} onChange={e => setTicketForm({ ...ticketForm, description: e.target.value })} required /></div>
                            <div className="row g-3"><div className="col-6"><label className="form-label fw-semibold">Type</label><select className="form-select" value={ticketForm.type} onChange={e => setTicketForm({ ...ticketForm, type: e.target.value })}><option value="corrective">Corrective</option><option value="inspection">Inspection</option><option value="other">Other</option></select></div><div className="col-6"><label className="form-label fw-semibold">Priority</label><select className="form-select" value={ticketForm.priority} onChange={e => setTicketForm({ ...ticketForm, priority: e.target.value })}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div></div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}><button type="button" className="btn btn-light" onClick={() => setShowTicketModal(false)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={savingTicket}>{savingTicket ? 'Creating...' : t('myTasks.createWorkOrderButton')}</button></div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
